import { Avatar, Button, Checkbox, Input, Modal } from "@deligoo/ui"
import { unwrapResult } from "@reduxjs/toolkit"
import dayjs from "dayjs"
import { FormEvent, useState } from "react"
import { toast } from "react-toastify"

import { useAppDispatch, useAppSelector } from "@/App"
import { requestReassignOrder, selectOrderById } from "@/store/orders"
import { selectCurrentTeamId } from "@/store/teams"
import { selectCurrentUser } from "@/store/user"
import { Driver, Order } from "@/types"
import { DriverUtils } from "@/utils"

import cls from "../OrderContextMenu.module.scss"
import { SelectDriverModal } from "./SelectDriverModal"

type ReassignOrderModalProps = {
  orderId: Order["id"]
  handleClose: () => void
}

export const ReassignOrderModal = ({ orderId, handleClose }: ReassignOrderModalProps) => {
  const team_id = useAppSelector(selectCurrentTeamId)
  const order = useAppSelector(selectOrderById(orderId))
  const currentUser = useAppSelector(selectCurrentUser)

  const [selectDriverModal, setSelectDriverModal] = useState(false)

  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null)
  const [forceAssignmentWithoutBreak, setForceAssignmentWithoutBreak] = useState<boolean>(false)
  const [forceAssignmentWithBreak, setForceAssignmentWithBreak] = useState<boolean>(false)
  const [pickupAt, setPickupAt] = useState<string>(getDefaultPickupAt(order?.pickup_at))

  const dispatch = useAppDispatch()

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const toastOptions = {
      toastId: "reassignOrder",
      updateId: "reassignOrder",
    }

    if (!team_id || !pickupAt) {
      toast.error("Nie wybrano czasu kompletacji.", toastOptions)

      return
    }

    const res = await dispatch(
      requestReassignOrder({
        orderId,
        body: {
          team_id,
          proposed_driver_id: selectedDriver?.id,
          pickup_at: pickupAt,
          force_assignment_without_break: forceAssignmentWithoutBreak,
          force_assignment_with_break: forceAssignmentWithBreak,
        },
      })
    ).then(unwrapResult)

    if (res.ok) {
      toast.success("Pomyślnie przepięto kuriera.", toastOptions)
    }

    handleClose()
  }

  return (
    <Modal handleClose={handleClose} className={cls.reassignOrderModal} closeBtnPosition="inside">
      <div className={cls.content}>
        <form onSubmit={handleSubmit}>
          <div className="h300 mb-8">Przepinanie kuriera</div>

          <Button
            onClick={() => setSelectDriverModal(true)}
            className={cls.proposedDriver}
            variation="outlined"
            color="muted"
            fullWidth
          >
            <div className={cls.proposedDriverContent}>
              {selectedDriver ? (
                <>
                  <Avatar
                    className="mr-5"
                    color={DriverUtils.getAvatarColor(selectedDriver)}
                    indicator={DriverUtils.getAvatarIndicator(selectedDriver)}
                    dotColor={DriverUtils.getAvatarDotColor(selectedDriver)}
                  >
                    {selectedDriver.name}
                  </Avatar>
                  {selectedDriver.name}
                </>
              ) : (
                <>
                  <Avatar className="mr-5">+</Avatar>
                  Dopasuj kuriera
                </>
              )}
            </div>
          </Button>

          <div className="h200 my-8">Czas kompletacji</div>

          <div className="d-flex align-center justify-between">
            <Input
              value={pickupAt}
              onChange={(e) => setPickupAt(e.currentTarget.value)}
              min={dayjs().add(1, "minute").format("HH:mm")}
              type="time"
              required
              plain
            />

            {currentUser?.force_assignment_enabled && (
              <div className={cls.options}>
                <Checkbox
                  className={cls.option}
                  id="forceAssignmentWithoutBreak"
                  onChange={() => setForceAssignmentWithoutBreak((currentValue) => !currentValue)}
                  disabled={forceAssignmentWithBreak}
                >
                  Wymuś przypisanie (nie uwzględnia innych zleceń - bez dodawania przerwy)
                </Checkbox>

                <Checkbox
                  className={cls.option}
                  id="forceAssignmentWithBreak"
                  onChange={() => setForceAssignmentWithBreak((currentValue) => !currentValue)}
                  disabled={forceAssignmentWithoutBreak}
                >
                  Wymuś przypisanie (z automatycznym dodaniem przerwy)
                </Checkbox>
              </div>
            )}
          </div>

          <Button color="primary" className="mt-8" type="submit" autoFocus>
            Przepnij kuriera
          </Button>
        </form>
      </div>

      {selectDriverModal && (
        <SelectDriverModal
          orderId={orderId}
          setSelectedDriver={setSelectedDriver}
          handleClose={() => setSelectDriverModal(false)}
        />
      )}
    </Modal>
  )
}

function getDefaultPickupAt(pickupAt: Order["pickup_at"] | undefined) {
  const now = dayjs()
  if (!pickupAt) return now.add(10, "minutes").format("HH:mm")

  const currentPickupAt = dayjs(pickupAt)
  const diffToNow = currentPickupAt.diff(now, "minute")
  if (diffToNow < 10) return now.add(10, "minutes").format("HH:mm")

  return currentPickupAt.format("HH:mm")
}
